import type { ProjectType } from "@acme/types";
import { imageCdnFullWidthProps } from "@acme/ui/image-cdn";
import { Link } from "@tanstack/react-router";
import { Image } from "@unpic/react";
import * as m from "~/paraglide/messages.js";
import ProjectCard from "./project-card";
import { ProjectStatusTag } from "./project-status-tag";

interface ProjectsProps {
  projects: ProjectType[];
}

/**
 * The listing, in two parts because the design says two things.
 *
 * Delivered work leads at full width: it is the evidence, and there is usually
 * one piece of it. Concepts follow in a grid under their own heading and a note
 * saying what they are, so nothing on this page can be mistaken for a claim.
 */
export default function Projects({ projects }: Readonly<ProjectsProps>) {
  if (projects.length === 0) {
    return (
      <div className="archive-empty">
        <h2>{m.projects_empty_title()}</h2>
        <p>{m.projects_empty_description()}</p>
      </div>
    );
  }

  const delivered = projects.filter((p) => p.status === "delivered");
  const concepts = projects.filter((p) => p.status === "concept");
  const [lead, ...restDelivered] = delivered;

  return (
    <>
      {lead ? (
        <Link
          className="project-feature"
          params={{ projectId: lead.slug }}
          to="/projects/$projectId"
        >
          {lead.imageUrl ? (
            <div className="project-feature-media">
              <Image
                alt=""
                fetchPriority="high"
                layout="fullWidth"
                loading="eager"
                sizes="(max-width: 720px) 100vw, 1100px"
                src={lead.imageUrl}
                {...imageCdnFullWidthProps(lead.imageUrl)}
              />
            </div>
          ) : null}
          <div className="project-feature-copy">
            <div>
              <ProjectStatusTag status={lead.status} />
              <h2>{lead.title}</h2>
            </div>
            <p>{lead.description}</p>
            <span className="read-link">
              {m.projects_view()} <span aria-hidden="true">↗</span>
            </span>
          </div>
        </Link>
      ) : null}

      {/* The rest of the delivered work, under no heading: the page label
          already calls all of it selected work. */}
      {restDelivered.length > 0 ? (
        <div className="portfolio-concepts">
          <div className="portfolio-grid">
            {restDelivered.map((project) => (
              <ProjectCard key={project.slug} project={project} />
            ))}
          </div>
        </div>
      ) : null}

      {/* The concepts get their own heading and their own disclaimer, so
          nothing under it can be read as delivered work. */}
      {concepts.length > 0 ? (
        <section
          aria-labelledby="concept-heading"
          className="portfolio-concepts"
        >
          <div className="related-heading">
            <h2 id="concept-heading">{m.projects_concepts_title()}</h2>
            <span className="small-note">{m.projects_concepts_note()}</span>
          </div>
          <div className="portfolio-grid">
            {concepts.map((project) => (
              <ProjectCard key={project.slug} project={project} />
            ))}
          </div>
          <p className="portfolio-note">{m.projects_disclaimer()}</p>
        </section>
      ) : null}
    </>
  );
}
